// MIT License - Copyright (c) 2026 Doug McNall

/**
 * Recreation Manager
 * Manages recreation facilities and activities for crew morale and relationships
 */

import STORAGE_KEYS from '../system/STORAGE_KEYS';

export const RECREATION_FACILITIES = {
  GYM: {
    id: 'gym',
    name: 'Fitness Center',
    description: 'Physical training facility',
    icon: '💪',
    cost: 5000,
    moraleBonus: 3,
    relationshipBonus: 2,
    activities: ['workout', 'sparring', 'meditation']
  },
  REC_ROOM: {
    id: 'rec_room', 
    name: 'Recreation Room',
    description: 'Games and entertainment',
    icon: '🎮',
    cost: 3000,
    moraleBonus: 5,
    relationshipBonus: 4,
    activities: ['games', 'movies', 'music']
  },
  HOLODECK: {
    id: 'holodeck',
    name: 'Holodeck',
    description: 'Virtual reality chamber',
    icon: '🌌',
    cost: 15000,
    moraleBonus: 8,
    relationshipBonus: 3,
    activities: ['simulation', 'training', 'adventure']
  }
};

export const RECREATION_ACTIVITIES = {
  workout: {
    name: 'Physical Training',
    description: '{crew} works out in the fitness center',
    moraleChange: 4,
    duration: 45,
    facility: 'gym'
  },
  sparring: {
    name: 'Combat Training',
    description: '{crew1} and {crew2} practice combat techniques',
    moraleChange: 3,
    relationshipChange: 5,
    xpChange: 2,
    duration: 60,
    facility: 'gym',
    requiresPartner: true
  },
  games: {
    name: 'Gaming Session',
    description: '{crew} plays games in the rec room',
    moraleChange: 6,
    duration: 30,
    facility: 'rec_room'
  },
  simulation: {
    name: 'Holodeck Adventure',
    description: '{crew} explores virtual worlds',
    moraleChange: 10,
    duration: 90,
    facility: 'holodeck'
  }
};

class RecreationManager {
  constructor() {
    this.facilities = new Set();
    this.activeActivities = [];
    this.loadFromStorage();
  }

  /**
   * Purchase recreation facility
   */
  purchaseFacility(facilityId, credits) {
    const facility = RECREATION_FACILITIES[facilityId.toUpperCase()];
    if (!facility) {
      return { success: false, message: 'Unknown facility' };
    }

    if (this.facilities.has(facilityId)) {
      return { success: false, message: 'Facility already installed' };
    }

    if (credits < facility.cost) {
      return { success: false, message: 'Insufficient credits' };
    }

    this.facilities.add(facilityId);
    this.saveToStorage();

    return {
      success: true,
      cost: facility.cost,
      message: `Installed ${facility.name}`
    };
  }

  /**
   * Start recreation activity
   */
  startActivity(crewId, activityId, partnerId = null) {
    const activity = RECREATION_ACTIVITIES[activityId];
    if (!activity) return { success: false, message: 'Unknown activity' };

    if (!this.facilities.has(activity.facility)) {
      return { success: false, message: 'Required facility not available' };
    }

    const newActivity = {
      id: `activity_${Date.now()}`,
      crewId,
      partnerId,
      activityId,
      startTime: Date.now(),
      duration: activity.duration * 60000 // Convert to milliseconds
    };

    this.activeActivities.push(newActivity);
    this.saveToStorage();

    return {
      success: true,
      activity: newActivity,
      message: `Started ${activity.name}`
    };
  }

  /**
   * Complete activities and apply benefits
   */
  processActivities() {
    const now = Date.now();
    const completed = [];

    this.activeActivities = this.activeActivities.filter(activity => {
      if (now >= activity.startTime + activity.duration) {
        completed.push(activity);
        return false;
      }
      return true;
    });

    if (completed.length > 0) {
      this.saveToStorage();
    }

    return completed.map(activity => {
      const activityData = RECREATION_ACTIVITIES[activity.activityId];
      return {
        crewId: activity.crewId,
        partnerId: activity.partnerId,
        moraleChange: activityData.moraleChange || 0,
        relationshipChange: activityData.relationshipChange || 0,
        xpChange: activityData.xpChange || 0,
        message: activityData.description
      };
    });
  }

  /**
   * Get available activities for crew member
   */
  getAvailableActivities(crewId) {
    return Object.entries(RECREATION_ACTIVITIES)
      .filter(([_, activity]) => this.facilities.has(activity.facility))
      .map(([id, activity]) => ({ id, ...activity }));
  }

  /**
   * Get facility bonuses
   */
  getFacilityBonuses() {
    let totalMoraleBonus = 0;
    let totalRelationshipBonus = 0;

    this.facilities.forEach(facilityId => {
      const facility = RECREATION_FACILITIES[facilityId.toUpperCase()];
      if (facility) {
        totalMoraleBonus += facility.moraleBonus;
        totalRelationshipBonus += facility.relationshipBonus;
      }
    });

    return { moraleBonus: totalMoraleBonus, relationshipBonus: totalRelationshipBonus };
  }

  /**
   * Get installed facilities
   */
  getInstalledFacilities() {
    return Array.from(this.facilities).map(id => ({
      id,
      ...RECREATION_FACILITIES[id.toUpperCase()]
    }));
  }

  /**
   * Get available facilities for purchase
   */
  getAvailableFacilities() {
    return Object.entries(RECREATION_FACILITIES)
      .filter(([id]) => !this.facilities.has(id.toLowerCase()))
      .map(([id, facility]) => ({ id: id.toLowerCase(), ...facility }));
  }

  /**
   * Check if facility is installed
   */
  hasFacility(facilityId) {
    return this.facilities.has(facilityId);
  }

  /**
   * Get active activities
   */
  getActiveActivities() {
    return [...this.activeActivities];
  }

  /**
   * Save to localStorage
   */
  saveToStorage() {
    try {
      const data = {
        facilities: Array.from(this.facilities),
        activeActivities: this.activeActivities
      };
      localStorage.setItem(STORAGE_KEYS.RECREATION, JSON.stringify(data));
    } catch (error) {
      console.error('[RecreationManager] Failed to save:', error);
    }
  }

  /**
   * Load from localStorage
   */
  loadFromStorage() {
    try {
      const data = localStorage.getItem(STORAGE_KEYS.RECREATION);
      if (data) {
        const parsed = JSON.parse(data);
        this.facilities = new Set(parsed.facilities || []);
        this.activeActivities = parsed.activeActivities || [];
      }
    } catch (error) {
      console.error('[RecreationManager] Failed to load:', error);
    }
  }

  /**
   * Reset recreation system
   */
  reset() {
    this.facilities.clear();
    this.activeActivities = [];
    this.saveToStorage();
  }
}

// Singleton instance
const recreationManager = new RecreationManager();
export default recreationManager;